import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';

@Component({
  selector: 'app-contactus',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './contactus.html',
  styleUrl: './contactus.css',
})
export class Contactus {

    constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta,
  ) {}

  ngOnInit() {
      this.setSeo();
  }


  private setSeo(): void {

  // =========================================================
  // SEO DATA
  // =========================================================

  const pageTitle =
    'Contact Red Monk Wellness | Mumbai & Kolkata Clinics';

  const description =
    'Contact Red Monk Wellness for appointments and enquiries at our Mumbai and Kolkata clinics. Find clinic addresses, phone numbers and contact details.';

  const keywords =
    'Red Monk Wellness contact, IV therapy Mumbai, IV therapy Kolkata, IV therapy clinic Mumbai, IV therapy clinic Kolkata, IV drip Mumbai, IV drip Kolkata, wellness IV therapy Mumbai, cosmetic IV therapy Mumbai, IV therapy consultation Mumbai, Red Monk Wellness Mumbai, Red Monk Wellness Kolkata';

  const canonicalUrl =
    'https://www.redmonkwellness.com/contactus';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/services/houseof-hydration-thumb.png';

  const sitemapUrl =
    'https://www.redmonkwellness.com/sitemap.xml';

  // =========================================================
  // TITLE
  // =========================================================

  this.title.setTitle(pageTitle);

  // =========================================================
  // BASIC META
  // =========================================================

  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'keywords',
    content: keywords
  });

  this.meta.updateTag({
    name: 'robots',
    content:
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  this.meta.updateTag({
    name: 'viewport',
    content: 'width=device-width, initial-scale=1'
  });

  this.meta.updateTag({
    name: 'google-site-verification',
    content: '7QzARYHwPFlrxb_DWbe2CdSeHLHRU-oTXeKHpi89MXg'
  });

  // =========================================================
  // OPEN GRAPH
  // =========================================================

  this.meta.updateTag({
    property: 'og:type',
    content: 'website'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content:
      'Get in touch with Red Monk Wellness for IV Therapy and wellness services in Mumbai and Kolkata. Call, WhatsApp or email our team for enquiries and consultations.'
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'Contact Red Monk Wellness for IV Therapy'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/png'
  });

  // =========================================================
  // TWITTER / X
  // =========================================================

  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content:
      'Contact Red Monk Wellness for IV Therapy and wellness services in Mumbai and Kolkata. Enquire about personalized IV therapy and consultations.'
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'Contact Red Monk Wellness'
  });

  // =========================================================
  // CANONICAL URL
  // =========================================================

  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  // =========================================================
  // SITEMAP
  // =========================================================

  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute('href', sitemapUrl);

  // =========================================================
  // REMOVE PREVIOUS CONTACT PAGE JSON-LD
  // =========================================================

  this.document
    .querySelectorAll('script[data-seo-schema="contact-page"]')
    .forEach((script) => script.remove());

  // =========================================================
  // HELPER FUNCTION FOR JSON-LD
  // =========================================================

  const addJsonLd = (schema: object): void => {
    const script = this.document.createElement('script');

    script.type = 'application/ld+json';

    script.setAttribute(
      'data-seo-schema',
      'contact-page'
    );

    script.textContent = JSON.stringify(schema);

    this.document.head.appendChild(script);
  };

  // =========================================================
  // ORGANIZATION SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.redmonkwellness.com/#organization',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png'
    },
    description:
      'Red Monk Wellness offers skin, hair, aesthetic and wellness treatments through its clinics in Mumbai and Kolkata.',
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  });

  // =========================================================
  // MEDICAL BUSINESS SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    '@id': 'https://www.redmonkwellness.com/#medicalbusiness',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo:
      'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png',
    description:
      'Red Monk Wellness offers wellness and cosmetic IV therapy services in Mumbai and Kolkata.',
    email: 'info@redmonkwellness.com',
    telephone: '+91-6262662662',
    areaServed: [
      {
        '@type': 'City',
        name: 'Mumbai'
      },
      {
        '@type': 'City',
        name: 'Kolkata'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  });

  // =========================================================
  // MUMBAI CLINIC SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    '@id': `${canonicalUrl}#mumbai`,
    name: 'Red Monk Wellness - Mumbai',
    url: canonicalUrl,
    telephone: '+91-6262662662',
    address: {
      '@type': 'PostalAddress',
      streetAddress:
        'Unit No. B-36, Ground Floor A Wing, Laxmi Business Park, Laxmi Industrial Estate, Sab TV Lane, Off Link Road, Andheri West',
      addressLocality: 'Mumbai',
      addressRegion: 'Maharashtra',
      postalCode: '400053',
      addressCountry: 'IN'
    },
    parentOrganization: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    }
  });

  // =========================================================
  // KOLKATA CLINIC SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    '@id': `${canonicalUrl}#kolkata`,
    name: 'Red Monk Wellness - Kolkata',
    url: canonicalUrl,
    telephone: '+91-6262030303',
    address: {
      '@type': 'PostalAddress',
      streetAddress:
        'Crescent Tower, Lala Lajpat Rai Sarani, 229, Acharya Jagadish Chandra Bose Rd, Sreepally, Elgin',
      addressLocality: 'Kolkata',
      addressRegion: 'West Bengal',
      postalCode: '700020',
      addressCountry: 'IN'
    },
    parentOrganization: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    }
  });

  // =========================================================
  // CONTACT PAGE SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    headline: 'Contact Red Monk Wellness',
    description:
      'Contact Red Monk Wellness for appointments and enquiries at its Mumbai and Kolkata clinics.',
    isPartOf: {
      '@type': 'WebSite',
      '@id': 'https://www.redmonkwellness.com/#website',
      name: 'Red Monk Wellness',
      url: 'https://www.redmonkwellness.com/'
    },
    about: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    },
    breadcrumb: {
      '@id': `${canonicalUrl}#breadcrumb`
    },
    inLanguage: 'en-IN'
  });

  // =========================================================
  // BREADCRUMB SCHEMA
  // =========================================================

  addJsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.redmonkwellness.com/home'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Contact Us',
        item: canonicalUrl
      }
    ]
  });

}


}
